// Domains & disputes — NEW console page (GAP-4, 18G §2.4 "Domains &
// disputes (rollup-fed + bridge actions)", D-25). Promotes the read-only
// domain/dispute columns that lived inside TrustOrgMetricsPage (18H §4.2)
// into their own focused surface, and adds the "bridge actions" half the
// manifest names: a per-org "Resolve dispute" action that calls the new
// POST /trust/licenses/:id/disputes/resolve route, which in turn invokes the
// TPRM7 `platform-trust-domains` bridge fn (declared in types/trust.ts /
// tprm7-bridge.ts).
//
// Data source: the same D-33 daily rollup mirror as org-metrics (GET
// /trust/org-metrics) — TPRM7 has no per-domain or per-dispute-case bridge
// READ yet (trust_verified_domains / trust_ownership_events are TPRM7-only
// tables), so this page works at the same org-aggregate grain the rollup
// already provides (verified_domains / domains_in_grace / open_disputes
// counts), honestly labelled as such. The RESOLVE action is real and fully
// wired end-to-end; it fails closed with a normal error toast until TPRM7
// ships the `platform-trust-domains` counterpart (see the inline note near
// the resolve panel).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { RefreshCw, Globe2, ShieldAlert, Info } from 'lucide-react';
import { formatRelativeTime } from '@/lib/utils';
import { personaBase } from '@/lib/persona';
import { Kpi } from '@/components/shared/Kpi';
import { StateFrame, classifyApiError, type LoadState } from '@/components/shared/StateFrame';
import { TableRowSkeleton } from '@/components/shared/SkeletonLoader';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { useToast } from '@/components/shared/ToastProvider';
import {
  getOrgMetrics, getSaasDetails, resolveDomainDispute, orgNameFor, tierLabel,
  type TrustOrgMetricsRow, type SaasLicenseTier, type TrustDisputeResolution,
} from '@/lib/api/trust';

const btnSec: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
  borderRadius: 8, background: 'var(--surface)', color: 'var(--text-primary)',
  border: '1px solid var(--border)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
};
const btnPrimary: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px',
  borderRadius: 8, background: 'var(--accent)', color: '#fff', border: '1px solid var(--accent)',
  fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
};
const th: React.CSSProperties = { padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.05em' };
const td: React.CSSProperties = { padding: '10px 12px', fontSize: 12.5 };
const inputSm: React.CSSProperties = { padding: '6px 9px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 12.5, outline: 'none', fontFamily: 'inherit' };

const RESOLUTION_LABEL: Record<TrustDisputeResolution, string> = {
  upheld: 'Uphold current claim',
  reassigned: 'Reassign to disputing org',
  dismissed: 'Dismiss dispute',
};

interface ResolveDraft {
  domain: string;
  resolution: TrustDisputeResolution;
  note: string;
}

function newDraft(): ResolveDraft {
  return { domain: '', resolution: 'upheld', note: '' };
}

export function DomainsDisputesPage() {
  const location = useLocation();
  const base = personaBase(location.pathname);
  const { toast } = useToast();
  const [rows, setRows] = useState<TrustOrgMetricsRow[]>([]);
  const [tierByLicense, setTierByLicense] = useState<Record<string, SaasLicenseTier>>({});
  const [state, setState] = useState<LoadState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draft, setDraft] = useState<ResolveDraft>(newDraft());
  const [submitting, setSubmitting] = useState(false);
  const [rowError, setRowError] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setState('loading');
    setErrorMessage(null);
    try {
      const data = await getOrgMetrics();
      setRows(data || []);
      try {
        const details = await getSaasDetails();
        const map: Record<string, SaasLicenseTier> = {};
        for (const d of details) map[d.license_id] = d.tier;
        setTierByLicense(map);
      } catch {
        setTierByLicense({});
      }
      setState('ready');
    } catch (err) {
      setRows([]);
      setState(classifyApiError(err));
      setErrorMessage(err instanceof Error ? err.message : 'Failed to load');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Domain/dispute activity only — the registry is uninteresting for an org
  // with zero verified domains, zero grace, zero disputes.
  const active = useMemo(() => rows.filter((r) => r.metrics.verified_domains > 0 || r.metrics.domains_in_grace > 0 || r.metrics.open_disputes > 0), [rows]);

  const kpis = useMemo(() => {
    const verified = rows.reduce((s, r) => s + (r.metrics.verified_domains || 0), 0);
    const grace = rows.reduce((s, r) => s + (r.metrics.domains_in_grace || 0), 0);
    const disputes = rows.reduce((s, r) => s + (r.metrics.open_disputes || 0), 0);
    return { verified, grace, disputes, orgsAffected: active.length };
  }, [rows, active]);

  const openResolve = (id: string) => {
    setActiveId(id);
    setDraft(newDraft());
    setRowError((prev) => ({ ...prev, [id]: '' }));
  };
  const closeResolve = () => setActiveId(null);

  const submitResolve = async (row: TrustOrgMetricsRow) => {
    if (!draft.domain.trim()) {
      setRowError((prev) => ({ ...prev, [row.id]: 'Domain is required.' }));
      return;
    }
    if (!row.license?.id) {
      setRowError((prev) => ({ ...prev, [row.id]: 'No license bound to this org.' }));
      return;
    }
    setSubmitting(true);
    setRowError((prev) => ({ ...prev, [row.id]: '' }));
    try {
      await resolveDomainDispute(row.license.id, {
        domain: draft.domain.trim(),
        resolution: draft.resolution,
        note: draft.note.trim() || null,
      });
      toast({ kind: 'success', title: `Dispute resolution recorded for ${draft.domain.trim()}.` });
      setActiveId(null);
      await load();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to resolve dispute';
      setRowError((prev) => ({ ...prev, [row.id]: msg }));
      toast({ kind: 'error', title: 'Resolve failed', body: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const skeleton = (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 22 }}>
        {[0, 1, 2, 3].map((i) => <div key={i} style={{ height: 92, borderRadius: 12, background: 'var(--bg-muted)' }} />)}
      </div>
      <div className="card" style={{ padding: 0 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>{[0, 1, 2, 3, 4].map((i) => <TableRowSkeleton key={i} cols={[130, 60, 80, 70, 80, 70, 90]} />)}</tbody>
        </table>
      </div>
    </>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Domains &amp; disputes</h1>
          <p className="page-subtitle">Verified-domain registry and open disputes per hosted trust org (D-25/D-54), rollup-fed from the same D-33 telemetry mirror as org metrics.</p>
        </div>
        <button onClick={load} style={btnSec}><RefreshCw size={13} /> Refresh</button>
      </div>

      <StateFrame
        state={state}
        skeleton={skeleton}
        onRetry={load}
        errorMessage={errorMessage}
        empty={rows.length === 0}
        emptyIcon="globe-2"
        emptyTitle="No domain activity yet"
        emptyBody="No daily rollup has landed via POST /telemetry/trust. This fills in once a hosted trust org has verified a domain or a dispute has been opened."
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 18 }}>
          <Kpi label="Verified domains" value={kpis.verified} icon="globe-2" accent="#16A34A" />
          <Kpi label="In grace" value={kpis.grace} icon="clock-3" accent={kpis.grace > 0 ? '#D97706' : undefined} />
          <Kpi label="Open disputes" value={kpis.disputes} icon="shield-alert" accent={kpis.disputes > 0 ? '#DC2626' : undefined} />
          <Kpi label="Orgs with domain activity" value={kpis.orgsAffected} icon="building-2" />
        </div>

        {active.length === 0 ? (
          <div className="card">
            <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              No org has a verified domain, grace-period domain, or open dispute yet.
            </div>
          </div>
        ) : (
          <div className="card" style={{ padding: 0 }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
                    <th style={th}>Org</th>
                    <th style={th}>Tier</th>
                    <th style={th}><Globe2 size={11} style={{ verticalAlign: -1 }} /> Verified</th>
                    <th style={th}>In grace</th>
                    <th style={th}><ShieldAlert size={11} style={{ verticalAlign: -1 }} /> Disputes</th>
                    <th style={th}>Last ingest</th>
                    <th style={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {active.map((r) => {
                    const tier = tierByLicense[r.license_id];
                    const hasDispute = r.metrics.open_disputes > 0;
                    const isActive = activeId === r.id;
                    return (
                      <React.Fragment key={r.id}>
                        <tr style={{ borderBottom: isActive ? 'none' : '1px solid var(--border)' }}>
                          <td style={td}>
                            {r.license?.id
                              ? <Link to={`${base}/licenses/${r.license.id}`} style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 500 }}>{orgNameFor(r)}</Link>
                              : orgNameFor(r)}
                          </td>
                          <td style={{ ...td, color: 'var(--text-secondary)' }}>{tier ? tierLabel(tier) : '—'}</td>
                          <td style={td}>{r.metrics.verified_domains}</td>
                          <td style={td}>
                            {r.metrics.domains_in_grace > 0 ? (
                              <span style={{ color: '#D97706', fontWeight: 600 }}>{r.metrics.domains_in_grace}</span>
                            ) : (
                              <span style={{ color: 'var(--text-muted)' }}>0</span>
                            )}
                          </td>
                          <td style={td}>
                            {hasDispute ? (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <StatusBadge status="open" /> {r.metrics.open_disputes}
                              </span>
                            ) : (
                              <StatusBadge status="resolved" />
                            )}
                          </td>
                          <td style={td}><span style={{ color: 'var(--text-muted)' }}>{formatRelativeTime(r.ingested_at)}</span></td>
                          <td style={td}>
                            {hasDispute && (
                              <button
                                onClick={() => (isActive ? closeResolve() : openResolve(r.id))}
                                style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid var(--border)', background: isActive ? 'var(--bg-muted)' : 'var(--surface)', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', fontFamily: 'inherit' }}
                              >
                                {isActive ? 'Cancel' : 'Resolve dispute'}
                              </button>
                            )}
                          </td>
                        </tr>
                        {isActive && (
                          <tr style={{ borderBottom: '1px solid var(--border)' }}>
                            <td colSpan={7} style={{ padding: '4px 12px 16px', background: 'var(--bg-secondary)' }}>
                              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', paddingTop: 10 }}>
                                <div>
                                  <label style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Domain</label>
                                  <input
                                    type="text" placeholder="example.com" value={draft.domain}
                                    onChange={(e) => setDraft((p) => ({ ...p, domain: e.target.value }))}
                                    style={{ ...inputSm, width: 180 }}
                                  />
                                </div>
                                <div>
                                  <label style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Resolution</label>
                                  <select
                                    value={draft.resolution}
                                    onChange={(e) => setDraft((p) => ({ ...p, resolution: e.target.value as TrustDisputeResolution }))}
                                    style={{ ...inputSm, minWidth: 200, background: 'var(--surface)' }}
                                  >
                                    {(Object.keys(RESOLUTION_LABEL) as TrustDisputeResolution[]).map((k) => (
                                      <option key={k} value={k}>{RESOLUTION_LABEL[k]}</option>
                                    ))}
                                  </select>
                                </div>
                                <div style={{ flex: 1, minWidth: 200 }}>
                                  <label style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Note (optional)</label>
                                  <input
                                    type="text" placeholder="Evidence reviewed, decision rationale…" value={draft.note}
                                    onChange={(e) => setDraft((p) => ({ ...p, note: e.target.value }))}
                                    style={{ ...inputSm, width: '100%' }}
                                  />
                                </div>
                                <button onClick={() => submitResolve(r)} disabled={submitting} style={btnPrimary}>
                                  {submitting ? 'Submitting…' : 'Submit resolution'}
                                </button>
                              </div>
                              {rowError[r.id] && (
                                <div style={{ marginTop: 8, fontSize: 12, color: '#DC2626' }}>{rowError[r.id]}</div>
                              )}
                              <div style={{ marginTop: 8, display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 11.5, color: 'var(--text-muted)' }}>
                                <Info size={12} style={{ marginTop: 1, flexShrink: 0 }} />
                                <span>
                                  This console tracks an <strong>aggregate open-dispute count</strong> for the org, not individual dispute cases — confirm the exact
                                  domain and evidence in TPRM7&rsquo;s own Domains tab before resolving. Submitting calls the platform bridge, which records the
                                  resolution and (once the corresponding TPRM7 endpoint is live) applies it to the domain claim.
                                </span>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </StateFrame>
    </div>
  );
}

export default DomainsDisputesPage;
